import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, Award, Clock, CheckCircle2,
  ChevronRight, Star, Users, Wrench
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicStatsPage = () => {
  const [stats, setStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stats/models`);
      if (res.ok) {
        const data = await res.json();
        setStats(data.stats || []);
        setSummary(data.summary || null);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-stats-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center gap-2 text-sm text-indigo-200 mb-4">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Estadísticas</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 flex items-center gap-4">
              <BarChart3 className="w-12 h-12" />
              Estadísticas de Reparaciones
            </h1>
            <p className="text-xl text-indigo-100 mb-8">
              Transparencia total. Consulta nuestras tasas de éxito reales por modelo de dispositivo.
            </p>
            
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold">{summary.total_repairs.toLocaleString()}+</p>
                  <p className="text-sm text-indigo-200">Reparaciones totales</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold">{summary.avg_success_rate}%</p>
                  <p className="text-sm text-indigo-200">Tasa de éxito media</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <p className="text-3xl font-bold">{summary.avg_satisfaction}</p>
                    <Star className="w-6 h-6 text-yellow-400 fill-current" />
                  </div>
                  <p className="text-sm text-indigo-200">Satisfacción media</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Stats List */}
            <div className="lg:col-span-2 space-y-4">
              <h2 className="text-xl font-bold mb-4">Estadísticas por Modelo</h2>
              
              {stats.map((stat, i) => (
                <motion.div
                  key={stat.model}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-white rounded-xl border p-6 cursor-pointer hover:shadow-lg transition-all ${
                    selectedModel?.model === stat.model ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-gray-200'
                  }`}
                  onClick={() => setSelectedModel(stat)}
                  data-testid={`stat-card-${stat.model.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">{stat.model}</h3>
                      <p className="text-sm text-gray-500">{stat.brand}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        <span className="text-2xl font-bold text-green-600">{stat.success_rate}%</span>
                      </div>
                      <p className="text-xs text-gray-500">tasa de éxito</p>
                    </div>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                      style={{ width: `${stat.success_rate}%` }}
                    />
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Reparaciones</p>
                      <p className="font-semibold">{stat.total_repairs.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Tiempo medio</p>
                      <p className="font-semibold">{stat.avg_repair_time_days} días</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Coste medio</p>
                      <p className="font-semibold">{stat.avg_cost}€</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Satisfacción</p>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="font-semibold">{stat.customer_satisfaction}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Detail Panel */}
            <div className="lg:col-span-1">
              {selectedModel ? (
                <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-4">
                  <div className="mb-6">
                    <h2 className="text-xl font-bold">{selectedModel.model}</h2>
                    <p className="text-gray-500">{selectedModel.brand}</p>
                  </div>

                  {/* Big Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{selectedModel.success_rate}%</p>
                      <p className="text-xs text-green-700">Tasa de éxito</p>
                    </div>
                    <div className="bg-indigo-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-indigo-600">{selectedModel.total_repairs}</p>
                      <p className="text-xs text-indigo-700">Reparaciones</p>
                    </div>
                  </div>

                  {/* Common Issues */}
                  <div className="mb-6">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Wrench className="w-4 h-4" />
                      Problemas más comunes
                    </h3>
                    <div className="space-y-3">
                      {selectedModel.most_common_issues?.map((issue, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{issue.issue}</span>
                            <span className="text-xs text-gray-500">{issue.percentage}%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-indigo-500 rounded-full"
                                style={{ width: `${issue.percentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-green-600">{issue.success_rate}% éxito</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Repeat Rate */}
                  <div className="p-4 bg-yellow-50 rounded-lg mb-6">
                    <p className="text-sm">
                      <strong className="text-yellow-700">Tasa de reincidencia:</strong>{' '}
                      <span className="text-yellow-800">{selectedModel.repeat_issue_rate}%</span>
                    </p>
                    <p className="text-xs text-yellow-600 mt-1">
                      Porcentaje de dispositivos que vuelven por el mismo problema
                    </p>
                  </div>

                  <Link to="/warranty/new">
                    <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                      Solicitar Reparación
                    </Button>
                  </Link>
                  
                  <p className="text-xs text-gray-400 text-center mt-3">
                    Datos actualizados: {selectedModel.last_updated}
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                  <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    Selecciona un modelo para ver estadísticas detalladas
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Trust Section */}
      <div className="bg-white py-16 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-6">¿Por qué publicamos estas estadísticas?</h2>
          <p className="text-gray-600 mb-8">
            Creemos en la transparencia total. Estos datos son reales y se actualizan regularmente.
            Queremos que tomes una decisión informada antes de confiar en nosotros para reparar tu dispositivo.
          </p>
          <div className="flex justify-center gap-8">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span className="text-sm">Datos verificables</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              <span className="text-sm">Actualización mensual</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-500" />
              <span className="text-sm">Sin filtros ni engaños</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicStatsPage;
